import { useEffect } from 'react'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import { useStores } from 'stores/utils/hooks/useStores'
import { getVoucherAmount } from './useVoucherAmount'
import type { VoucherTopBannerModel } from '../VoucherBanner.types'
import { trackVoucherAmountTier } from '../helpers/trackVoucherActivation'

const trackedBannerIdentities = new WeakMap<object, Set<string>>()

interface UseVoucherBannerTrackingInput {
  model: VoucherTopBannerModel
  flow: 'standard' | 'rce'
  sourceAmount: number
  decisionKey: string
}

const useVoucherBannerTracking = ({
  model,
  flow,
  sourceAmount,
  decisionKey
}: UseVoucherBannerTrackingInput) => {
  const { rootStore } = useRootStore()
  const {
    traversal: { data, traversalId }
  } = useStores()

  useEffect(() => {
    if (model.kind === 'hidden') return

    // Use the tiered (ffg) mapping as the canonical tracking split: 20k+ -> 100, 50k+ -> 150.
    // This keeps tracking buckets tenant-agnostic and avoids smava's special 30k exclusion.
    const trackedVoucherAmount = getVoucherAmount(sourceAmount, 'ffg')

    if (trackedVoucherAmount === null) return

    const amountTier = String(trackedVoucherAmount)
    const identity = [traversalId, flow, amountTier, decisionKey].join(':')
    const identities = trackedBannerIdentities.get(rootStore) || new Set()

    if (identities.has(identity)) return

    identities.add(identity)
    trackedBannerIdentities.set(rootStore, identities)
    trackVoucherAmountTier(data, sourceAmount)
  }, [
    data,
    decisionKey,
    flow,
    model.kind,
    rootStore,
    sourceAmount,
    traversalId
  ])
}

export default useVoucherBannerTracking
